import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import type { LiveStatusDoc, Location } from '../types';

export function useLiveBoard(location: Location) {
  const [courts, setCourts] = useState<LiveStatusDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'liveStatus'),
      where('location', '==', location)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs
          .map((d) => {
            const data = d.data();
            return {
              ...data,
              id: d.id,
              court: data.court ?? '',
              judgeName: data.judgeName ?? [],
              judgeHeading: data.judgeHeading,
              judgeImageUrls: data.judgeImageUrls || [],
              currentItemNumber: data.currentItemNumber ?? null,
              currentCaseNumber: data.currentCaseNumber ?? null,
              status: data.status ?? 'idle',
              polledAt: data.polledAt?.toMillis?.() ?? data.polledAt ?? 0,
              lastCaseAt: data.lastCaseAt?.toMillis?.() ?? data.lastCaseAt ?? null,
              currentCaseDetail: data.currentCaseDetail
                ? {
                    ...data.currentCaseDetail,
                    judgeName: data.currentCaseDetail.judgeName ?? [],
                    respondents: data.currentCaseDetail.respondents ?? [],
                    advocates: data.currentCaseDetail.advocates ?? [],
                    connectedMatters: data.currentCaseDetail.connectedMatters ?? [],
                    lastUpdated:
                      data.currentCaseDetail.lastUpdated?.toMillis?.() ??
                      data.currentCaseDetail.lastUpdated ?? 0,
                  }
                : null,
            } as LiveStatusDoc;
          })
          .filter(doc => doc.status !== 'no_data')
          .sort((a, b) => {
            // Basic natural sort: try extracting numbers to sort "1" before "2a" before "11"
            const courtA = String(a.court || '');
            const courtB = String(b.court || '');
            const numA = parseInt(courtA, 10);
            const numB = parseInt(courtB, 10);
            const isNumA = !isNaN(numA);
            const isNumB = !isNaN(numB);
            if (isNumA && isNumB && numA !== numB) return numA - numB;
            return courtA.localeCompare(courtB);
          });
        setCourts(docs);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return unsub;
  }, [location]);

  return { courts, loading, error };
}
